import { useMutation } from '@tanstack/react-query';

import { useEnsureCsrfToken } from '~/context/CsrfContext';
import type { ToastData } from '~/hooks/useToast';
import type { CommunityPost } from '~/interfaces/community';
import { NectarPageBlock } from '~/utils/nectarPageBlock';
import { withNectarTrackingParams } from '~/utils/nectarTracking';

export type CreateCommunityPostResponse =
  | { post: CommunityPost; toast?: ToastData }
  | { id: string; title?: string; tiptap_state?: CommunityPost['tiptap_state']; updated_at?: string; toast?: ToastData }
  | { toast: ToastData };

const isCreateCommunityPostResponse = (data: unknown): data is CreateCommunityPostResponse => {
  if (typeof data !== 'object' || data === null) return false;
  return 'post' in data || 'id' in data || 'toast' in data;
};

const useCreateCommunityPostMutation = () => {
  const ensureCsrfToken = useEnsureCsrfToken();
  return useMutation({
    mutationFn: async (payload: Record<string, string>): Promise<CreateCommunityPostResponse> => {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch('/community_post', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          'X-CSRF-Token': csrfToken,
        },
        body: new URLSearchParams(withNectarTrackingParams(payload, NectarPageBlock.CommunityPostForm)),
      });

      const data: unknown = await response.json();
      if (!isCreateCommunityPostResponse(data)) throw new Error('composer_invalid_response');

      return data;
    },
  });
};

export default useCreateCommunityPostMutation;
